
import GoogleCalendarReminder from "@/components/calendar/GoogleCalendarReminder.vue";
import { DossierConfigService } from "@/services/dossier-config-service";
import { GoogleService, ModalService } from "@/services";
import { v4 } from "uuid";
import { GoogleCalendarService } from "@/services/google-calendar-service";
import { IGoogleCalendarEventGroup } from "@/utils/google-calendar-event-group";
import Vue from "vue";
import { ISelectItem } from "winfakt-vue-components";
import AppDateTimeEdit from "./DateTimeEdit.vue";

export default Vue.extend({
	data() {
		return {
			fetchedCalendars: [] as gapi.client.calendar.CalendarListEntry[],
			startTime: new Date(),
			endTime: new Date(),
			allDay: false,
			reminderTimeSpan: "minutes",
			reminders: [] as any[],
			autocompleteLocation: DossierConfigService.getContactsConfig().AutoCompleteContactAddress,
			locationSessionToken: GoogleService.createAutocompleteAddressSession(),
			locationResults: [] as google.maps.places.PlacePrediction[],
			locationThreadId: "",
			editingDescription: !(this as any).event?.event?.description
		};
	},
	props: {
		event: {
			type: Object as () => IGoogleCalendarEventGroup
		},
		calendars: {
			type: Array as () => gapi.client.calendar.CalendarListEntry[]
		},
	},
	computed: {
		locationItems(): ISelectItem<google.maps.places.PlacePrediction>[] {
			return this.locationResults.map(p => {
				return {
					text: p.text.text,
					value: p
				};
			});
		},
		calendarItems(): ISelectItem<string>[] {
			let cals = this.calendars;
			if (!cals) {
				cals = this.fetchedCalendars;
			}
			return cals.map(c => {
				return {
					text: c.summary,
					value: c.id
				};
			});
		},
	},
	watch: {
		"event.event.start.dateTime": {
			immediate: true,
			handler(val: string) {

				if (val != this.startTime.toISOString()) {
					this.startTime = new Date(val);
				}
			}
		},
		"event.event.end.dateTime": {
			immediate: true,
			handler(val: string) {
				if (val != this.endTime.toISOString()) {
					this.endTime = new Date(val);
				}
			}
		},
		"event.event.start.date": {
			immediate: true,
			handler(val: string) {
				if (val)
					this.startTime = new Date(val);
			}
		},
		"event.event.end.date": {
			immediate: true,
			handler(val: string) {
				if (val)
					this.endTime = new Date(val);
			}
		},
		allDay() {
			if (this.allDay) {
				delete this.event.event.start.dateTime;
				delete this.event.event.end.dateTime;
				this.event.event.start.date = this.startTime.toISOString().substr(0, 10);
				this.event.event.end.date = this.endTime.toISOString().substr(0, 10);
			} else {
				delete this.event.event.start.date;
				delete this.event.event.end.date;
				this.event.event.start.dateTime = this.startTime.toISOString();
				this.event.event.end.dateTime = this.endTime.toISOString();
			}
		},
		reminders: {
			immediate: false,
			deep: true,
			handler(){
				this.event.event.reminders = {
					useDefault: false,
					overrides: [...this.reminders]
				};
			}
		}
	},
	methods: {
		setLocation(value: string) {
			this.$set(this.event.event, "location", value);
			if (this.autocompleteLocation) {
				this.autocompleteLocationSuggestions();
			}
		},
		async autocompleteLocationSuggestions() {
			let threadId = v4();
			this.locationThreadId = threadId;
			await new Promise(resolve => setTimeout(resolve, 500));
			if (this.locationThreadId != threadId) return;
			this.locationResults = await GoogleService.autocompletAddress(this.event.event.location || "", this.locationSessionToken);
		},
		openMap() {
			let location = this.event.event.location;
			if (!location) return;
			let url = `https://www.google.com/maps/embed?pb=!1m3!2m1!1s${encodeURIComponent(location)}!6i15`;
			ModalService.openIframeModal(url, location, {width: 900, height: 650, plain: true, subtitle: this.$ct("labels.location").toString()});
		},
		onLocationClicked(item: ISelectItem<google.maps.places.PlacePrediction>) {
			this.$set(this.event.event, "location", item.value.text.text);
			this.locationResults = [];
		},
		onStartInput(date: Date) {
			if (this.allDay) {
				this.event.event.start.date = date.toISOString().substr(0, 10);
			} else {
				this.event.event.start.dateTime = date.toISOString();
			}
		},
		onEndInput(date: Date) {
			if (this.allDay) {
				this.event.event.end.date = date.toISOString().substr(0, 10);
			} else {
				this.event.event.end.dateTime = date.toISOString();
			}
		},
		addReminder() {
			if (!this.event.event.reminders) {
				this.event.event.reminders = {useDefault: false};
			};
			if (!this.reminders) {
				this.reminders = [];
			};
			let override = {
				method: "email" as gapi.client.calendar.ReminderMethod,
				minutes: 10 as gapi.client.calendar.integer,
			};
			this.reminders.push(override);
		},
		removeReminder(index: number) {
			if (!this.event.event.reminders) return;
			if (!this.reminders) return;
			this.reminders.splice(index, 1);
		},
	},
	async mounted() {
		if (!this.calendars || this.calendars.length == 0) {
			this.fetchedCalendars = await GoogleCalendarService.getCalendars();
			if (this.fetchedCalendars.length > 0 && !this.event.calendarId){
				this.event.calendarId = this.fetchedCalendars[0].id;
			}

			if (this.event.event.start.date)
				this.allDay = true;
		}else if (!this.event.calendarId){
			this.event.calendarId = this.calendars[0].id;
		}
		if (this.event.event.reminders && this.event.event.reminders.overrides){
			this.reminders.push(...this.event.event.reminders.overrides);
		}
	},
	components: {
		AppDateTimeEdit,
		GoogleCalendarReminder
	},
	i18nEx: {
		componentPrefix: "components.calendar"
	}
});
